<template>
    <div class="inline-flex flex-wrap">
        <span v-if="!!term" class="ks-badge mt-1" :class="{ 'mr-1': filtersCount > 0, 'muted': muted, 'info': !muted }">
            <strong>Term</strong>:
            {{term}}
            <a v-if="!readOnly" href="#" @click.prevent="$emit('clear-term')">
                <vault-icon :icon="icon_times"></vault-icon>
            </a>
        </span>
        <span
            v-for="(filter, key, index) in expandedFilters"
            :key="'active-filter-' + index"
            class="ks-badge mt-1 text-sm"
            :class="{
                'mr-1': !!term || (index < filtersCount - 1),
                'muted': muted,
                'info': !muted && !isUnsupported(filter),
                'strike-through bg-gray-100 text-gray-600': isUnsupported(filter),
             }"
            :title="isUnsupported(filter) ? 'Filter is only active on the Matrix view' : null"
            style="text-overflow:ellipsis;overflow:hidden;"
        >
            <template v-if="filter.filter_name">
                <strong>{{filter.filter_name}}</strong><template v-if="filter.filter_label">:</template>
            </template>
            <template v-else>
                <strong>{{ formatKey(key) }}</strong>:
            </template>
            {{filter.filter_label}}
            <a v-if="!readOnly" href="#" @click.prevent="$emit('clear', filter)">
                <vault-icon :icon="icon_times" :class="{ 'text-gray-600': isUnsupported(filter) }"></vault-icon>
            </a>
        </span>
        <a v-if="!readOnly && showClearAll && filtersAndTermCount > 1"
            href="#"
            class="mt-1 ml-2 py-1 text-xs uppercase nowrap"
            @click.prevent="$emit('clear-all')"
        >
            Clear All
        </a>
    </div>
</template>

<script>
    import {faTimes} from '@fortawesome/pro-light-svg-icons/faTimes';
    import { snakeToTitle } from 'helpers/strings.js';
    import { formatDateWithin, formatDateUpcoming, formatDatePeriod } from 'helpers/dates.js';

    export default {
        name: 'SearchFiltersDisplay',

        props: {
            filters: {
                type: Object,
                required: true,
            },

            readOnly: {
                type: Boolean,
                default: false,
            },

            term: {
                type: String,
                default: '',
            },

            muted: {
                type: Boolean,
                default: false,
            },

            showClearAll: {
                type: Boolean,
                default: false,
            },

            unsupportedKeys: {
                type: Array,
                default: () => [],
            },
        },

        computed: {
            filtersCount() {
                return Object.keys(this.expandedFilters).length;
            },

            filtersAndTermCount() {
                return !!this.term
                    ? this.filtersCount + 1
                    : this.filtersCount;
            },

            expandedFilters() {
                return Object.entries(this.filters)
                    .reduce((filters, [key, value]) => {
                        let filter_key = value.filter_key;
                        let filter_label = value.filter_label;
                        let filter_name = value.filter_name;
                        let filter_value = value.value;

                        // hide filter
                        if ( filter_name === false ) {
                            return filters;
                        }

                        if ( filter_value ) {
                            // filter object - expand subkeys into legacy filters
                            for ( let sub_key in filter_value ) {

                                // handle date 'within'
                                if ( this.checkDate(key, sub_key, 'within') ) {
                                    filters[`${key}_${sub_key}`] = {
                                        filter_key: filter_value[sub_key],
                                        filter_label: formatDateWithin(filter_value[sub_key]),
                                        filter_name: snakeToTitle(key),
                                        key,
                                        sub_key,
                                    };
                                    continue;
                                }

                                // handle date 'upcoming'
                                if ( this.checkDate(key, sub_key, 'upcoming') ) {
                                    filters[`${key}_${sub_key}`] = {
                                        filter_key: filter_value[sub_key],
                                        filter_label: formatDateUpcoming(filter_value[sub_key]),
                                        filter_name: snakeToTitle(key),
                                        key,
                                        sub_key,
                                    };
                                    continue;
                                }

                                // handle date 'period'
                                if ( this.checkDate(key, sub_key, 'period') ) {
                                    filters[`${key}_${sub_key}`] = {
                                        filter_key: filter_value[sub_key],
                                        filter_label: formatDatePeriod(filter_value[sub_key]),
                                        filter_name: snakeToTitle(key),
                                        key,
                                        sub_key,
                                    };
                                    continue;
                                }

                                filters[`${key}_${sub_key}`] = {
                                    filter_key: filter_value[sub_key],
                                    filter_label: filter_value[sub_key],
                                    filter_name: snakeToTitle(key) + ': ' + snakeToTitle(sub_key),
                                    key,
                                    sub_key,
                                };
                            }
                        } else if ( ['', undefined, null].includes(filter_label) ) {
                            // no filter label = only show the key
                            filters[key] = {
                                filter_name: filter_name || snakeToTitle(key),
                                key,
                            };

                        } else {
                            // legacy filter
                            filters[key] = {
                                filter_key: filter_key || key,
                                filter_label: filter_label || filter_value || value,
                                filter_name: filter_name || snakeToTitle(key),
                                key,
                            };
                        }

                        return filters;
                    }, {});
            },
        },

        data() {
            return {
                icon_times: faTimes,
            };
        },

        methods: {
            checkDate(key, sub_key, value) {
                return key.slice(-5) === '_date' && sub_key === value;
            },

            isUnsupported(filter) {
                return this.unsupportedKeys.includes(filter.key);
            },

            formatKey(key) {
                return snakeToTitle(key);
            },
        },
    }
</script>
